import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import { getModulePermissions } from "../../utils/permissionUtils";

// Density Part 3 — launch-pocket overview + THE Activate button.
//
// Counts shown are REAL members (test accounts excluded, waitlist+active),
// the same population the hourly activation check counts, so this card
// predicts exactly what the cron will decide. Activation is deliberate and
// manual: the button only arms once the cron has flipped the pocket to
// pending_activation, and the server endpoint is idempotent (double-clicks
// and retries can never re-send the launch blast).

const STATUS_BADGE = {
  waitlist: { label: "Waitlist", className: "bg-neutral-200 text-neutral-600" },
  pending_activation: { label: "Ready to activate", className: "bg-warning-100 text-warning-600" },
  active: { label: "Live", className: "bg-success-100 text-success-600" },
};

const PocketsCard = () => {
  const [pockets, setPockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null); // pocket id in flight
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [outOfPocket, setOutOfPocket] = useState(null); // {count, byCity, users}
  const [sweeping, setSweeping] = useState(false);
  // Threshold editing (2026-08-21). The activation gate used to be a
  // hand-typed production database write; this is the same two numbers with
  // validation, an audit trail, and the arithmetic spelled out on save.
  const [editingThreshold, setEditingThreshold] = useState(null); // pocket id
  const [draft, setDraft] = useState({
    total: "",
    balanceRatioMax: "",
    maxPairDistanceKm: "",
    ageMin: "",
    ageMax: "",
  });
  const [savingThreshold, setSavingThreshold] = useState(false);
  // Founding cap per city (founder 2026-09-28). The app's founding screen
  // says this number ("Only 150 people in Miami..."), read live from the
  // server, so saving here changes the screen with no app build.
  const [editingCap, setEditingCap] = useState(null); // pocket id
  const [capDraft, setCapDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);

  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const { shouldCheckPermissions, canEdit } = getModulePermissions("admin-settings");

  const fetchPockets = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/pockets/stats`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPockets(response.data?.pockets || []);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage("Error fetching pockets");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL, authToken, navigate]);

  const fetchOutOfPocket = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/pockets/out-of-pocket`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setOutOfPocket(response.data || null);
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
      }
      // Non-fatal: the pockets table above still works without this section.
    }
  }, [BASE_URL, authToken, navigate]);

  useEffect(() => {
    fetchPockets();
    fetchOutOfPocket();
  }, [fetchPockets, fetchOutOfPocket]);

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/pockets/absorb-sweep`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { checked, absorbed, notified, absorbedUsers } = response.data || {};
      setSuccessMessage(
        absorbed > 0
          ? `Sweep: ${absorbed} of ${checked} absorbed (${notified} notified): ${(absorbedUsers || [])
              .map((u) => `${u.name || "?"} to ${u.pocket}`)
              .join(", ")}`
          : `Sweep: checked ${checked ?? 0}, nobody qualifies for a pocket yet.`
      );
      setTimeout(() => setSuccessMessage(""), 8000);
      fetchPockets();
      fetchOutOfPocket();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "Sweep failed, nothing changed");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setSweeping(false);
    }
  };

  const startEditThreshold = (pocket) => {
    setEditingThreshold(pocket.id);
    setDraft({
      total: String(pocket.activationThreshold?.total ?? ""),
      balanceRatioMax: String(pocket.activationThreshold?.balanceRatioMax ?? ""),
      // Blank means no pocket limit, so it must survive the round trip as
      // blank rather than becoming a number.
      maxPairDistanceKm:
        pocket.maxPairDistanceKm == null ? "" : String(pocket.maxPairDistanceKm),
      // Age band (founder 2026-09-04). ageBand arrives as [min, max].
      ageMin: String(pocket.ageBand?.[0] ?? ""),
      ageMax: String(pocket.ageBand?.[1] ?? ""),
    });
  };

  const handleSaveThreshold = async (pocket) => {
    const total = Number(draft.total);
    const cap = Number(draft.balanceRatioMax);
    const radiusRaw = String(draft.maxPairDistanceKm).trim();
    const radius = radiusRaw === "" ? null : Number(radiusRaw);
    if (!Number.isInteger(total) || !Number.isFinite(cap)) {
      setErrorMessage("Target must be a whole number and the balance cap a decimal like 0.6");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    if (radius !== null && !Number.isInteger(radius)) {
      setErrorMessage("Pairing radius must be a whole number of km, or blank for no limit");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    // Age band (founder 2026-09-04): whole numbers, min not above max. The
    // server re-validates against 18-99 and runs the who-moves sweep.
    const ageMin = Number(draft.ageMin);
    const ageMax = Number(draft.ageMax);
    if (!Number.isInteger(ageMin) || !Number.isInteger(ageMax)) {
      setErrorMessage("Age range needs two whole numbers");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    if (ageMin > ageMax) {
      setErrorMessage("Age minimum cannot be above the maximum");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    const majorityMax = total - (Math.floor((1 - cap) * total) + 1);
    const bandChanged =
      ageMin !== (pocket.ageBand?.[0] ?? null) || ageMax !== (pocket.ageBand?.[1] ?? null);
    const sure = window.confirm(
      [
        `Set ${pocket.name} to ${total} members with a balance cap of ${cap}?`,
        `The largest gender group would then be allowed at most ${majorityMax} of ${total}.`,
        radius === null
          ? "Pairing radius: no pocket limit, so each member's own distance setting is the only limit."
          : `Pairing radius: people in this pocket are only introduced to each other within ${radius} km.`,
        `Age range: ${ageMin} to ${ageMax}.` +
          (bandChanged
            ? " Widening lets waiting members in immediately (you'll see who). Narrowing never removes anyone already in."
            : ""),
        "Nothing launches and nobody is notified. The doors still only open when you press Activate.",
      ].join("\n\n")
    );
    if (!sure) return;

    setSavingThreshold(true);
    try {
      const response = await axios.patch(
        `${BASE_URL}/admin/pockets/${pocket.id}/threshold`,
        { total, balanceRatioMax: cap, maxPairDistanceKm: radius, ageMin, ageMax },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { after, demands, now, changed, pairing, bandSweep } = response.data || {};
      const sweepText = bandSweep
        ? bandSweep.moved.length === 0
          ? " Age range saved; nobody was waiting on it."
          : ` Now in: ${bandSweep.moved
              .map((m) => `${m.name || m.email} (${m.age})`)
              .join(", ")}.`
        : "";
      setSuccessMessage(
        (changed && changed.length === 0
          ? `${pocket.name} threshold unchanged (${after?.total} / ${after?.balanceRatioMax}).`
          : `${pocket.name} threshold is now ${after?.total} members, balance cap ${after?.balanceRatioMax}, ages ${after?.ageMin} to ${after?.ageMax}.`) +
          sweepText +
          (demands ? ` ${demands.plainEnglish}` : "") +
          (pairing ? ` ${pairing}` : "") +
          (now ? ` Today: ${now.members} counted, ${now.readinessPercent}% ready.` : "")
      );
      setTimeout(() => setSuccessMessage(""), 12000);
      setEditingThreshold(null);
      fetchPockets();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(
        error.response?.data?.message || "Threshold not saved, nothing changed"
      );
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleSaveFoundingCap = async (pocket) => {
    const next = Number(String(capDraft).trim());
    if (!Number.isInteger(next)) {
      setErrorMessage("Founding cap must be a whole number");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    // Numbers EVER given out (founder 2026-10-06: "capped at 250, ever").
    const given = pocket.founding?.issued ?? 0;
    const sure = window.confirm(
      [
        `Set the founding cap for ${pocket.name} to ${next}?`,
        `The app's founding screen will say "Only ${next} people in ${pocket.name} will ever hold this badge."`,
        `${given} numbers have been given out, so ${Math.max(0, next - given)} would remain.`,
        "Numbers are never taken back or given out twice. A cap at or below that number closes the city's founding badges.",
      ].join("\n\n")
    );
    if (!sure) return;
    setSavingCap(true);
    try {
      const response = await axios.patch(
        `${BASE_URL}/admin/pockets/${pocket.id}/founding-cap`,
        { foundingCap: next },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { after } = response.data || {};
      setSuccessMessage(
        `${pocket.name} founding cap is now ${after?.cap}: ${after?.issued ?? 0} given out, ${after?.remaining ?? 0} remaining.`
      );
      setTimeout(() => setSuccessMessage(""), 8000);
      setEditingCap(null);
      fetchPockets();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "Founding cap not saved, nothing changed");
      setTimeout(() => setErrorMessage(""), 6000);
    } finally {
      setSavingCap(false);
    }
  };

  const handleActivate = async (pocket) => {
    const sure = window.confirm(
      `Activate ${pocket.name}?\n\nEvery waitlisted member moves to active and the launch notification is sent. This is the real launch. It cannot be quietly undone.`
    );
    if (!sure) return;

    setActivating(pocket.id);
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/pockets/${pocket.id}/activate`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const { membersActivated, blast, alreadyActive } = response.data || {};
      setSuccessMessage(
        alreadyActive && blast?.sent === false
          ? `${pocket.name} was already live, nothing re-sent.`
          : `${pocket.name} is live: ${membersActivated ?? 0} members activated, launch notification ${blast?.sent ? "sent" : "already sent earlier"}.`
      );
      setTimeout(() => setSuccessMessage(""), 6000);
      fetchPockets();
    } catch (error) {
      if (error.response?.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      setErrorMessage(error.response?.data?.message || "Activation failed, nothing was sent");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setActivating(null);
    }
  };

  // Every pocket carries the same rule; read it once for the blurb.
  const countingRule = pockets[0]?.countingRule ?? "all_real_members";

  const ratioText = (ratio, cap) => {
    if (ratio === null || ratio === undefined) return "N/A";
    const pct = Math.round(ratio * 100);
    return `${pct}/${100 - pct}${cap ? ` (needs better than ${Math.round(cap * 100)}/${100 - Math.round(cap * 100)})` : ""}`;
  };

  // "Men 4 / Women 1" — real members only, same population as the counts.
  const GENDER_LABELS = { man: "Men", woman: "Women", beyond_binary: "Beyond binary" };
  const GENDER_ORDER = ["man", "woman", "beyond_binary"];
  const splitText = (byGender) => {
    const entries = Object.entries(byGender || {}).filter(([, n]) => n > 0);
    if (!entries.length) return "N/A";
    entries.sort(
      ([a], [b]) =>
        (GENDER_ORDER.indexOf(a) + 1 || 99) - (GENDER_ORDER.indexOf(b) + 1 || 99)
    );
    return entries
      .map(([g, n]) => `${GENDER_LABELS[g] || g.charAt(0).toUpperCase() + g.slice(1)} ${n}`)
      .join(" / ");
  };

  return (
    <div className="card mt-4">
      <div className="card-body">
        {successMessage && <SuccessAlert message={successMessage} />}
        {errorMessage && <DengerAlert message={errorMessage} />}

        <div className="mb-4 pb-2 border-bottom">
          <p className="fw-bold mb-1">Launch Pockets</p>
          <small className="text-muted">
            Member counts are the same numbers the hourly activation check
            uses: test accounts excluded, and
            {countingRule === "gatekeeper_complete"
              ? " only members who finished the Gatekeeper."
              : " every real member, whether or not they finished the Gatekeeper."}{" "}
            When a pocket crosses its threshold it becomes “Ready to activate”
            and you get a bell notification; nothing launches until you press
            Activate. “Doors open at” is editable, and holds three numbers: the
            member target, the balance cap, and the pairing radius in km. The
            pocket’s city list decides who can join the city; the pairing
            radius decides how far apart two people may be introduced. Changing
            any of them never launches anything and never notifies anyone.
            “Founding badges” is how many founding numbers the city will ever
            give out; the app’s founding screen says that number, so changing
            it changes the screen right away, no app update needed.
          </small>
        </div>

        {loading ? (
          <p className="text-muted mb-0">Loading pockets…</p>
        ) : (
          <div className="table-responsive">
            {/* bordered-table brings the dark theme, but its min-width:
                max-content assumes short cells - this card's prose cells
                blew the table out to several screens wide (2026-09-04).
                The inline override restores one-screen wrapping. */}
            <table
              className="table bordered-table align-middle mb-0"
              style={{ minWidth: 0, width: "100%" }}
            >
              <thead>
                <tr>
                  <th>Pocket</th>
                  <th>Status</th>
                  <th>Members</th>
                  <th>Gender balance</th>
                  <th>Doors open at</th>
                  <th>Founding badges</th>
                  <th style={{ width: "1%" }}></th>
                </tr>
              </thead>
              <tbody>
                {pockets.map((pocket) => {
                  const badge = STATUS_BADGE[pocket.status] || {
                    label: pocket.status,
                    className: "bg-neutral-200 text-neutral-600",
                  };
                  const target = pocket.activationThreshold?.total;
                  const cap = pocket.activationThreshold?.balanceRatioMax;
                  // Fall back to realMembers so the card still renders
                  // correctly against a backend that predates the counted
                  // fields.
                  const counted = pocket.countedMembers ?? pocket.realMembers;
                  const excluded = (pocket.realMembers ?? 0) - (counted ?? 0);
                  return (
                    <tr key={pocket.slug}>
                      <td>
                        <span className="fw-semibold">{pocket.name}</span>{" "}
                        <small className="text-muted">({pocket.slug})</small>
                        {pocket.isReviewPocket && (
                          <span className="badge bg-info-100 text-info-600 ms-2">App review</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${badge.className}`}>{badge.label}</span>
                      </td>
                      <td>
                        {counted}
                        {target ? ` / ${target}` : ""}
                        {excluded > 0 && (
                          <>
                            <br />
                            <small className="text-muted">
                              {excluded} more signed up but never finished the
                              Gatekeeper
                            </small>
                          </>
                        )}
                      </td>
                      <td>
                        <span className="fw-semibold">
                          {splitText(pocket.countedByGender || pocket.realByGender)}
                        </span>
                        <br />
                        <small className="text-muted">
                          {ratioText(
                            pocket.countedBalanceRatio ?? pocket.realBalanceRatio,
                            cap
                          )}
                        </small>
                      </td>
                      <td>
                        {pocket.isReviewPocket ? (
                          <small className="text-muted">Always open</small>
                        ) : editingThreshold === pocket.id ? (
                          // Labeled groups that wrap (2026-09-04): five bare
                          // boxes in one row clipped their values and read as
                          // a riddle. Each field says what it is.
                          <div className="d-flex flex-wrap align-items-end gap-2">
                            {[
                              { key: "total", label: "Members", min: 10, max: 10000, step: 1, width: 90 },
                              { key: "balanceRatioMax", label: "Balance cap", min: 0.5, max: 1, step: 0.01, width: 90 },
                              {
                                key: "maxPairDistanceKm",
                                label: "Radius km",
                                min: 5,
                                max: 500,
                                step: 1,
                                width: 90,
                                title: "Pairing radius in km. Blank means no pocket limit.",
                              },
                              {
                                key: "ageMin",
                                label: "Age min",
                                min: 18,
                                max: 99,
                                step: 1,
                                width: 80,
                                title: "Youngest age this city accepts. Widening lets waiting members in immediately; narrowing never removes anyone already in.",
                              },
                              {
                                key: "ageMax",
                                label: "Age max",
                                min: 18,
                                max: 99,
                                step: 1,
                                width: 80,
                                title: "Oldest age this city accepts. Widening lets waiting members in immediately; narrowing never removes anyone already in.",
                              },
                            ].map((f) => (
                              <div key={f.key}>
                                <small
                                  className="text-muted d-block mb-1"
                                  style={{ fontSize: 11, whiteSpace: "nowrap" }}
                                >
                                  {f.label}
                                </small>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: f.width }}
                                  value={draft[f.key]}
                                  min={f.min}
                                  max={f.max}
                                  step={f.step}
                                  title={f.title}
                                  aria-label={f.label}
                                  onChange={(e) =>
                                    setDraft({ ...draft, [f.key]: e.target.value })
                                  }
                                />
                              </div>
                            ))}
                            <div className="d-flex align-items-center gap-1">
                              <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                disabled={savingThreshold}
                                onClick={() => handleSaveThreshold(pocket)}
                              >
                                {savingThreshold ? "Saving…" : "Save"}
                              </button>
                              <button
                                className="btn btn-link btn-sm text-muted"
                                type="button"
                                disabled={savingThreshold}
                                onClick={() => setEditingThreshold(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="fw-semibold">{target ?? "N/A"}</span>{" "}
                            <small className="text-muted">
                              members, cap {cap ?? "N/A"}
                            </small>
                            <br />
                            <small className="text-muted">
                              {pocket.maxPairDistanceKm == null
                                ? "pairs at any distance both people allow"
                                : `pairs within ${pocket.maxPairDistanceKm} km`}
                            </small>
                            <br />
                            <small className="text-muted">
                              ages {pocket.ageBand?.[0] ?? "N/A"} to{" "}
                              {pocket.ageBand?.[1] ?? "N/A"}
                            </small>
                            <br />
                            <button
                              className="btn btn-link btn-sm p-0"
                              type="button"
                              disabled={shouldCheckPermissions && !canEdit}
                              onClick={() => startEditThreshold(pocket)}
                            >
                              Change
                            </button>
                          </>
                        )}
                      </td>
                      <td>
                        {pocket.isReviewPocket ? (
                          <small className="text-muted">Fixed at 10</small>
                        ) : editingCap === pocket.id ? (
                          <div className="d-flex flex-wrap align-items-end gap-2">
                            <div>
                              <small
                                className="text-muted d-block mb-1"
                                style={{ fontSize: 11, whiteSpace: "nowrap" }}
                              >
                                Founding cap
                              </small>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                style={{ width: 90 }}
                                value={capDraft}
                                min={Math.max(10, pocket.founding?.issued ?? 0)}
                                max={10000}
                                step={1}
                                aria-label="Founding cap"
                                onChange={(e) => setCapDraft(e.target.value)}
                              />
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                disabled={savingCap}
                                onClick={() => handleSaveFoundingCap(pocket)}
                              >
                                {savingCap ? "Saving…" : "Save"}
                              </button>
                              <button
                                className="btn btn-link btn-sm text-muted"
                                type="button"
                                disabled={savingCap}
                                onClick={() => setEditingCap(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <span className="fw-semibold">
                              {pocket.founding?.cap ?? "N/A"}
                            </span>{" "}
                            <small className="text-muted">cap</small>
                            <br />
                            <small className="text-muted">
                              {pocket.founding?.issued ?? 0} given out,{" "}
                              {pocket.founding?.remaining ?? "N/A"} remaining
                            </small>
                            <br />
                            <button
                              className="btn btn-link btn-sm p-0"
                              type="button"
                              disabled={shouldCheckPermissions && !canEdit}
                              onClick={() => {
                                setEditingCap(pocket.id);
                                setCapDraft(String(pocket.founding?.cap ?? ""));
                              }}
                            >
                              Change
                            </button>
                          </>
                        )}
                      </td>
                      <td>
                        {!pocket.isReviewPocket && pocket.status !== "active" && (
                          <button
                            className="btn btn-primary btn-sm"
                            type="button"
                            disabled={
                              pocket.status !== "pending_activation" ||
                              activating === pocket.id ||
                              (shouldCheckPermissions && !canEdit)
                            }
                            title={
                              pocket.status === "pending_activation"
                                ? "Launch this pocket"
                                : "Arms automatically when the pocket crosses its threshold"
                            }
                            onClick={() => handleActivate(pocket)}
                          >
                            {activating === pocket.id ? "Activating…" : "Activate"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Out of pocket — people no pocket serves yet. Invisible anywhere
            else in the dashboard; the hourly sweep absorbs them the moment
            a pocket's city list starts covering them. */}
        {outOfPocket && (
          <div className="mt-4">
            <div
              className="d-flex justify-content-between align-items-end mb-2 pb-2"
              style={{ borderBottom: "1px solid #e9ecef" }}
            >
              <div>
                <p className="fw-bold mb-1">
                  Out of pocket
                  <span className="badge bg-neutral-200 text-neutral-600 ms-2">
                    {outOfPocket.count}
                  </span>
                </p>
                <small className="text-muted">
                  Real people whose city no pocket serves yet. They see the
                  honest waiting screen; an hourly sweep absorbs and notifies
                  them the moment a pocket covers their city. Sweep now after
                  changing a pocket&apos;s city list.
                </small>
              </div>
              <button
                className="btn btn-outline-primary btn-sm text-nowrap"
                type="button"
                disabled={sweeping || (shouldCheckPermissions && !canEdit)}
                onClick={handleSweep}
              >
                {sweeping ? "Sweeping…" : "Sweep now"}
              </button>
            </div>

            {outOfPocket.count === 0 ? (
              <p className="text-muted mb-0">Nobody is stranded right now.</p>
            ) : (
              <div className="table-responsive">
                {/* bordered-table brings the dark theme, but its min-width:
                max-content assumes short cells - this card's prose cells
                blew the table out to several screens wide (2026-09-04).
                The inline override restores one-screen wrapping. */}
            <table
              className="table bordered-table align-middle mb-0"
              style={{ minWidth: 0, width: "100%" }}
            >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>City</th>
                      <th>Gender / Age</th>
                      <th>Signed up</th>
                      <th>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outOfPocket.users.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <span className="fw-semibold">{u.name || "?"}</span>{" "}
                          <small className="text-muted">{u.email}</small>
                          {u.isTestAccount && (
                            <span className="badge bg-info-100 text-info-600 ms-2">Test</span>
                          )}
                        </td>
                        <td>{u.city || "?"}</td>
                        <td>
                          {(u.gender || "?") + (u.age != null ? ` / ${u.age}` : "")}
                        </td>
                        <td>
                          <small>
                            {u.signedUpAt
                              ? new Date(u.signedUpAt).toLocaleDateString()
                              : "?"}
                          </small>
                        </td>
                        <td>
                          <small>
                            {u.lastSeen
                              ? new Date(u.lastSeen).toLocaleDateString()
                              : "?"}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PocketsCard;
